// src/pages/brand/BrandProjectDetail.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

type IAItem = {
  id: string;
  prompt: string;
  style: string;
  imageUrl?: string;
  createdAt?: string;
  resolution?: string | null;
  engine?: string | null;
  costCredits?: number | null;
  aspectRatio?: string | null;
};

function friendlyApiError(err?: unknown) {
  const raw = String(err || "").trim();
  if (!raw) return "Ocurrió un error. Intenta de nuevo.";
  if (raw === "maintenance_mode") return "El servidor está en mantenimiento. Intenta nuevamente en unos minutos.";
  if (raw === "rate_limited") return "Estamos recibiendo muchas solicitudes. Intenta de nuevo en unos segundos.";
  return raw;
}

function humanDate(iso?: string) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export default function BrandProjectDetail() {
  const { projectId } = useParams();
  const { user, getAccessToken, loading } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState<IAItem[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState("");

  const [mainId, setMainId] = useState<string | null>(null);
  const [mainLoaded, setMainLoaded] = useState(false);
  const lastMainUrlRef = useRef<string | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<IAItem | null>(null);

  const mainItem = useMemo(() => {
    if (!mainId) return null;
    return items.find((x) => x.id === mainId) || null;
  }, [items, mainId]);

  useEffect(() => {
    const url = mainItem?.imageUrl || null;
    if (url !== lastMainUrlRef.current) {
      lastMainUrlRef.current = url;
      setMainLoaded(false);
    }
  }, [mainItem?.imageUrl]);

  const openPreview = useCallback((item: IAItem) => {
    setPreviewItem(item);
    setPreviewOpen(true);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewItem(null);
  }, []);

  const load = useCallback(async () => {
    if (!user) return;
    if (!projectId) return;

    setError("");
    setLoadingList(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("No hay sesión activa.");

      const data = await api.generationsList(
        { projectId, sourceMode: "brand" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!data?.success || !Array.isArray((data as any).items)) {
        throw new Error(friendlyApiError((data as any)?.error || "No se pudo cargar la galería del proyecto."));
      }

      const mapped: IAItem[] = (data as any).items.map((g: any) => ({
        id: String(g.id),
        prompt: String(g.prompt || ""),
        style: String(g.style || "—"),
        imageUrl: typeof g.imageUrl === "string" ? g.imageUrl : undefined,
        createdAt: g.created_at,
        resolution: g.resolution ?? null,
        engine: g.engine ?? null,
        costCredits: typeof g.cost_credits === "number" ? g.cost_credits : null,
        aspectRatio: g.aspect_ratio ?? null,
      }));

      setItems(mapped);

      // set default main
      if (!mainId && mapped.length > 0) setMainId(mapped[0].id);
      if (mainId && !mapped.some((x) => x.id === mainId)) setMainId(mapped[0]?.id ?? null);
    } catch (e) {
      setItems([]);
      setError(e instanceof Error ? friendlyApiError(e.message) : "Error cargando galería.");
    } finally {
      setLoadingList(false);
    }
  }, [user, projectId, getAccessToken, mainId]);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    load();
  }, [loading, user, load]);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/login?next=" + encodeURIComponent(`/brand/projects/${projectId || ""}`), { replace: true });
  }, [loading, user, navigate, projectId]);

  if (!projectId) {
    return (
      <div className="p-6 text-white">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
          Proyecto inválido.
          <button
            onClick={() => navigate("/brand/projects")}
            className="ml-3 text-[12px] px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition"
          >
            Volver →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full text-white">
      <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gray-400">BRAND MODE</div>
            <h1 className="text-2xl md:text-3xl font-bold mt-2">Galería del proyecto</h1>
            <p className="text-gray-300/90 mt-2 text-sm md:text-base max-w-2xl leading-relaxed">
              Aquí ves exclusivamente las imágenes generadas dentro de este proyecto (sourceMode=brand).
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/brand/projects")}
              className="text-[11px] px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-gray-200"
            >
              ← Volver
            </button>
            <button
              onClick={load}
              className="text-[11px] px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-gray-200"
            >
              🔄 Refrescar
            </button>
            <button
              onClick={() => navigate(`/brand/studio?projectId=${encodeURIComponent(projectId)}`)}
              className="px-4 py-2 rounded-xl text-sm font-semibold
                bg-gradient-to-r from-indigo-500 to-purple-500
                hover:from-indigo-400 hover:to-purple-400 transition shadow-lg shadow-indigo-500/30"
            >
              ✨ Generar en este proyecto
            </button>
          </div>
        </div>

        {!!error && (
          <div className="rounded-2xl border border-red-400/20 bg-red-500/10 p-4 text-sm text-red-200">{error}</div>
        )}

        {/* MAIN CANVAS */}
        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md shadow-2xl shadow-indigo-900/35 overflow-hidden">
          <div className="relative w-full bg-black/40 h-[40vh] md:h-[52vh] max-h-[560px] min-h-[300px]">
            {mainItem?.imageUrl ? (
              <img
                src={mainItem.imageUrl}
                alt=""
                draggable={false}
                onLoad={() => setMainLoaded(true)}
                className={[
                  "w-full h-full object-cover",
                  "transition-all duration-500 ease-out",
                  mainLoaded ? "opacity-100 blur-0 scale-100" : "opacity-0 blur-md scale-[1.01]",
                ].join(" ")}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                {loadingList ? "Cargando…" : "Selecciona una miniatura para ver aquí."}
              </div>
            )}

            {mainItem && (
              <button
                type="button"
                onClick={() => openPreview(mainItem)}
                className="absolute top-4 right-4 text-[11px] px-3 py-2 rounded-xl border border-white/15 bg-black/35 hover:bg-white/10 transition backdrop-blur"
              >
                Abrir preview ↗
              </button>
            )}
          </div>

          <div className="p-4 md:p-5 border-t border-white/10">
            <div className="text-[12px] text-gray-200 font-semibold truncate">{mainItem?.prompt || "—"}</div>
            <div className="mt-1 text-[11px] text-gray-400 flex flex-wrap gap-2 items-center">
              <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">{mainItem?.style || "—"}</span>
              <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                {mainItem?.resolution ? String(mainItem.resolution).toUpperCase() : "—"}
              </span>
              <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                {mainItem?.costCredits ? `${mainItem.costCredits} créditos` : "—"}
              </span>
              <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                {mainItem?.aspectRatio || "—"}
              </span>
              <span className="text-gray-500">· {humanDate(mainItem?.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* THUMB GRID */}
        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md p-4 md:p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold">Imágenes del proyecto</div>
            <div className="text-[11px] text-gray-500">{loadingList ? "Cargando…" : `${items.length} elemento(s)`}</div>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {items.map((it) => (
              <button
                key={it.id}
                onClick={() => setMainId(it.id)}
                onDoubleClick={() => openPreview(it)}
                className={[
                  "text-left rounded-2xl border overflow-hidden transition",
                  mainId === it.id
                    ? "border-indigo-400/60 bg-indigo-500/10"
                    : "border-white/10 bg-black/25 hover:bg-white/5 hover:border-indigo-400/30",
                ].join(" ")}
                title={it.prompt}
              >
                <div className="relative">
                  {it.imageUrl ? (
                    <div className="w-full aspect-video overflow-hidden">
                      <img
                        src={it.imageUrl}
                        alt={it.prompt}
                        className="w-full h-full object-cover object-center"
                        draggable={false}
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-indigo-800/40 via-purple-800/40 to-slate-900/60" />
                  )}

                  <div className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded-full bg-black/55 border border-white/10 text-gray-100">
                    {(it.resolution || "—").toString().toUpperCase()}
                  </div>

                  <div className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded-full bg-black/55 border border-white/10 text-gray-100">
                    {it.costCredits ? `${it.costCredits}c` : "—"}
                  </div>
                </div>

                <div className="p-2.5">
                  <div className="text-[11px] text-gray-200 truncate">{it.prompt}</div>
                  <div className="mt-1 text-[10px] text-gray-400 truncate">
                    {it.style} · {it.aspectRatio || "—"}
                  </div>

                  <div className="mt-2 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openPreview(it);
                      }}
                      className="text-[10px] px-2 py-1 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                    >
                      Abrir ↗
                    </button>
                    <span className="text-[10px] text-gray-500">
                      {it.createdAt ? new Date(it.createdAt).toLocaleDateString() : "—"}
                    </span>
                  </div>
                </div>
              </button>
            ))}

            {!loadingList && items.length === 0 && (
              <div className="col-span-full text-center text-gray-500 text-sm py-10">
                <div className="text-gray-300 font-semibold">Este proyecto aún no tiene imágenes</div>
                <div className="mt-2 text-gray-500">
                  Genera desde el Brand Studio y quedarán guardadas aquí.
                </div>
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => navigate(`/brand/studio?projectId=${encodeURIComponent(projectId)}`)}
                    className="text-xs px-4 py-2 rounded-xl border border-white/15 hover:bg-white/10 transition text-gray-200"
                  >
                    Generar en este proyecto →
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-3 text-[11px] text-gray-500">
            Tip: Click = seleccionar para preview central · Doble click o “Abrir” = preview completo.
          </div>
        </div>

        {/* PREVIEW MODAL */}
        {previewOpen && previewItem && (
          <div className="fixed inset-0 z-50 bg-black/80 px-4 py-6 overflow-y-auto" onClick={closePreview}>
            <div
              className="w-full max-w-5xl mx-auto rounded-2xl border border-white/10 bg-[#0c0c14] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-white/10 flex items-start justify-between gap-4 shrink-0">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{previewItem.prompt}</div>
                  <div className="mt-1 text-[11px] text-gray-400 flex flex-wrap gap-2 items-center">
                    <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                      {(previewItem.resolution || "—").toString().toUpperCase()}
                    </span>
                    <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">{previewItem.style}</span>
                    <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                      {previewItem.costCredits ? `${previewItem.costCredits} créditos` : "—"}
                    </span>
                    <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                      {previewItem.aspectRatio || "—"}
                    </span>
                    <span className="text-gray-500">· {humanDate(previewItem.createdAt)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {previewItem.imageUrl && (
                    <a
                      href={previewItem.imageUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[11px] px-3 py-2 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                      title="Abrir imagen en nueva pestaña"
                    >
                      ↗ Abrir
                    </a>
                  )}
                  <button
                    onClick={closePreview}
                    className="text-[11px] px-3 py-2 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                  >
                    ✕ Cerrar
                  </button>
                </div>
              </div>

              <div className="p-4 overflow-y-auto">
                <div className="rounded-2xl border border-white/10 bg-black/40 overflow-auto">
                  {previewItem.imageUrl ? (
                    <img src={previewItem.imageUrl} alt={previewItem.prompt} className="w-full h-auto block" draggable={false} />
                  ) : (
                    <div className="h-80 bg-gradient-to-br from-indigo-800/40 via-purple-800/40 to-slate-900/60" />
                  )}
                </div>
                <div className="mt-3 text-[11px] text-gray-500">
                  Tip: abre la imagen en nueva pestaña y haz zoom al 200–300% para notar detalles.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
